import { request, ApiError } from './core';

export interface GalleryUpdate {
  title?: string;
  description?: string;
  cover_asset_id?: string;
  is_public?: boolean;
  allow_downloads?: boolean;
  client_id?: string;
  booking_id?: string;
  layout?: string;
  share_password?: string;
  mode?: string;
}

export interface Gallery {
  ID: string;
  UserID: string;
  ClientID: string;
  BookingID: string;
  Slug: string;
  Title: string;
  Description: string;
  CoverAssetID: string;
  CoverURL?: string;
  IsPublic: boolean;
  AllowDownloads: boolean;
  Layout: string;
  Mode: string;
  AssetCount: number;
  CreatedAt: string;
  UpdatedAt: string;
}

export interface GalleryAsset {
  id: string;
  filename: string;
  content_type: string;
  width: number;
  height: number;
  name: string;
  description: string;
  web_url: string;
  thumb_url: string;
  og_url: string;
  original_url?: string;
  created_at: string;
}

export interface AssetStub {
  id: string;
  gallery_id: string;
  gallery_title: string;
  filename: string;
  thumb_url: string;
  web_key: string;      // use this as the asset_id in block content
  width: number;
  height: number;
  is_favorite?: boolean;
}

export interface PublishResult {
  gallery: Gallery;
  email_sent: boolean;
  client_email: string;
  booking_updated: boolean;
}

export interface ProofingComment {
  id: string;
  display_name: string;
  body: string;
  created_at: string;
}

export interface ProofingAssetResult {
  asset_id: string;
  favourites: number;
  flags: number;
  comments: ProofingComment[];
}

export interface ProofingResults {
  gallery_id: string;
  gallery_slug: string;
  results: ProofingAssetResult[];
  total_favourites: number;
}

export const galleriesApi = {
  publishGallery: (slug: string, notify: boolean) =>
    request<PublishResult>("POST", `/api/v1/galleries/${encodeURIComponent(slug)}/publish`, { notify }),

  createGallery: (g: {
    Title: string;
    Slug: string;
    ClientID?: string;
    BookingID?: string;
    IsPublic?: boolean;
    AllowDownloads?: boolean;
  }) =>
    request<Gallery>("POST", "/api/v1/galleries", {
      title: g.Title,
      slug: g.Slug,
      client_id: g.ClientID ?? "",
      booking_id: g.BookingID ?? "",
      is_public: g.IsPublic ?? false,
      allow_downloads: g.AllowDownloads ?? true,
    }),
  updateGallery: (id: string, patch: GalleryUpdate) =>
    request<Gallery>("PUT", `/api/v1/galleries/${encodeURIComponent(id)}`, patch),
  listGalleries: (clientId?: string) =>
    request<Gallery[]>("GET", `/api/v1/galleries${clientId ? `?client_id=${encodeURIComponent(clientId)}` : ""}`),
  getGallery: (slug: string) =>
    request<Gallery>("GET", `/api/v1/galleries/${encodeURIComponent(slug)}`),
  listGalleryAssets: (slug: string) =>
    request<GalleryAsset[]>("GET", `/api/v1/galleries/${encodeURIComponent(slug)}/assets`),
  deleteGalleryAsset: (gallerySlug: string, assetId: string) =>
    request<void>("DELETE", `/api/v1/galleries/${encodeURIComponent(gallerySlug)}/assets/${encodeURIComponent(assetId)}`),
  updateGalleryAsset: (gallerySlug: string, assetId: string, patch: { name?: string; description?: string }) =>
    request<GalleryAsset>("PUT", `/api/v1/galleries/${encodeURIComponent(gallerySlug)}/assets/${encodeURIComponent(assetId)}`, patch),
  uploadGalleryAsset: async (
    galleryID: string,
    file: File,
  ): Promise<GalleryAsset> => {
    const form = new FormData();
    form.append("file", file);
    const res = await fetch(`/api/v1/galleries/${encodeURIComponent(galleryID)}/assets`, {
      method: "POST",
      body: form,
      credentials: "same-origin",
    });
    if (!res.ok) {
      const text = await res.text();
      let message = res.statusText;
      try {
        const parsed = JSON.parse(text) as { error?: string };
        if (parsed.error) message = parsed.error;
      } catch {
        /* ignore */
      }
      throw new ApiError(res.status, message);
    }
    return (await res.json()) as GalleryAsset;
  },

  getGalleryProofing: (slug: string) =>
    request<ProofingResults>("GET", `/api/v1/galleries/${encodeURIComponent(slug)}/proofing`),
  getGalleryPicks: (slug: string) =>
    request<{ gallery_slug: string; count: number; picks: Array<{ asset_id: string; filename: string; favourites: number }> }>(
      "GET", `/api/v1/galleries/${encodeURIComponent(slug)}/picks`,
    ),
  galleryPicksZipURL: (slug: string) => `/api/v1/galleries/${encodeURIComponent(slug)}/picks.zip`,
  getGalleryCull: (slug: string) =>
    request<{
      gallery_slug: string;
      count: number;
      unscored: number;
      duplicate_groups: number;
      assets: Array<{
        id: string;
        filename: string;
        thumb_url: string;
        sharpness: number | null;
        exposure: number | null;
        cluster_id: string;
        scored: boolean;
      }>;
    }>("GET", `/api/v1/galleries/${encodeURIComponent(slug)}/cull`),

  setGalleryRetention: (slug: string, days: number | null) =>
    request<{ retention_days: number | null }>("PUT", `/api/v1/galleries/${encodeURIComponent(slug)}/retention`, { retention_days: days }),

  // Asset library — returns all gallery assets for the authenticated user
  listAllAssets: () => request<AssetStub[]>("GET", "/api/v1/assets"),
  toggleAssetFavorite: (id: string, isFavorite: boolean) =>
    request<{ is_favorite: boolean }>("PUT", `/api/v1/assets/${encodeURIComponent(id)}/favorite`, { is_favorite: isFavorite }),

  checkGallerySlugAvailable: async (slug: string): Promise<boolean> => {
    try {
      await request<Gallery>("GET", `/api/v1/galleries/${encodeURIComponent(slug)}`);
      return false; // slug taken
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return true; // slug free
      throw e;
    }
  },
};
